// src/v2/hooks/useReportER2.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useReportER2 = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Scenario A: Download the raw PhilHealth template directly
   */
  const downloadBlankTemplate = useCallback((month, year) => {
    const pdfUrl = `${window.location.origin}/docs/PhilHealth_ER2.pdf`;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `PhilHealth_ER2_${month}_${year}_Blank.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showGeneralMessage("Blank template downloaded (No records found).", true);
  }, [showGeneralMessage]);

  /**
   * Scenario B: Fill the PhilHealth ER2 with data
   */
  const fillAndDownloadPDF = useCallback(async (data, month, year, companyInfo) => {
    try {
      const pdfUrl = `${window.location.origin}/docs/PhilHealth_ER2.pdf`;
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("Template not found");

      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const page = pdfDoc.getPages()[0];
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // --- MAPPING (Estimates for PhilHealth ER2 Layout) ---
      // 1. Employer Information
      page.drawText(companyInfo?.name || "", { x: 50, y: 720, size: 10, font });
      page.drawText(companyInfo?.tin || "", { x: 50, y: 700, size: 10, font });

      // 2. Report Period
      page.drawText(`${month} / ${year}`, { x: 450, y: 720, size: 10, font });

      // 3. Employee Rows (First row example)
      if (data && data.length > 0) {
        const first = data[0];
        page.drawText(first.fullName || "", { x: 50, y: 600, size: 8, font });
        page.drawText(first.philHealthNumber || "", { x: 200, y: 600, size: 8, font });
        page.drawText(first.position || "", { x: 300, y: 600, size: 8, font });
        page.drawText((first.salary || 0).toLocaleString(), { x: 450, y: 600, size: 8, font });
      }

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `PhilHealth_ER2_${month}_${year}.pdf`;
      link.click();

      showGeneralMessage("PhilHealth ER2 generated and downloaded.", true);
    } catch (error) {
      console.error(error);
      showGeneralMessage("Error filling PDF. Downloading blank instead.", false);
      downloadBlankTemplate(month, year);
    }
  }, [showGeneralMessage, downloadBlankTemplate]);

  const generateAndDownload = useCallback(async (params, month, year, companyInfo) => {
    setLoading(true);
    try {
      let data = [];
      try {
        const res = await reportsService.getReportData('ER2', params);
        data = res || [];
      } catch (apiErr) {
        console.warn("Backend not found. Falling back to Demo Mode.");
        data = [];
      }

      if (data.length === 0) {
        downloadBlankTemplate(month, year);
      } else {
        await fillAndDownloadPDF(data, month, year, companyInfo);
      }
    } catch (err) {
      showGeneralMessage("Failed to process request.", false);
    } finally {
      setLoading(false);
    }
  }, [fillAndDownloadPDF, downloadBlankTemplate, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};